import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Alert, Box, Button, Paper, Stack, Step, StepButton, Stepper, Typography, styled } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { useCallback, useMemo, useState } from 'react'
import { useMutation } from 'urql'
import { graphql } from '../../gql'
import { ThemedAgGrid } from '../ThemedAgGrid'
import usageGif from './usage.gif'

const steps = ['Download Template', 'Populate Template', 'Upload']

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

export default function ImportStepper() {
  const [activeStep, setActiveStep] = useState(0)
  const [completed, setCompleted] = useState<{
    [k: number]: boolean
  }>({})
  const [uploading, setUploading] = useState(false)

  const handleStep = (step: number) => () => {
    setActiveStep(step)
  }

  const [getUploadUrlMutationState, getUploadUrlMutation] = useMutation(
    graphql(`
      mutation SignedUploadUrl($input: SignedUrlUploadRequest!) {
        SignedUploadUrl(input: $input) {
          filepath
          url
        }
      }
    `),
  )

  const [importMutationState, importMutation] = useMutation(
    graphql(`
      mutation ImportMutation($input: ImportFromFileInput!) {
        ImportFromFile(input: $input) {
          clients {
            created
            skipped
            errors
          }
          vendors {
            created
            skipped
            errors
          }
          orders {
            created
            skipped
            errors
          }
        }
      }
    `),
  )
  const handleUploadFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setUploading(true)
      const f = event.target.files?.item(0)
      if (!f) return
      const { data } = await getUploadUrlMutation({
        input: {
          filename: f.name,
          contentType: f.type,
        },
      })
      if (!data?.SignedUploadUrl) {
        console.error('Failed to get signed url')
        return
      }
      const { url: uploadUrl, filepath } = data.SignedUploadUrl
      // post to the signed url
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        // headers: {
        //   'Content-Type': f.type, // <-- this caused some errors. Careful if you specify it.
        // },
        body: f,
      }).catch((e) => {
        console.error(e)
        setUploading(false)
        return
      })
      if (uploadResponse) {
        if (uploadResponse.status !== 200) {
          console.error('Failed to upload file')
          console.error(uploadResponse.statusText)
          const body = await uploadResponse.text()
          console.error(body)
          setUploading(false)
          return
        }
      }

      // trigger import
      await importMutation({
        input: {
          storagePath: filepath,
        },
      })
      setUploading(false)
    },
    [getUploadUrlMutation, importMutation],
  )

  const $uploadResults = useMemo(() => {
    if (!importMutationState.data) return null
    const importData = Object.entries(importMutationState.data.ImportFromFile)
      .filter(([k, v]) => {
        return !k.startsWith('__')
      })
      .map(([k, v]) => {
        return { entity: k, ...v }
      })
    console.log(importData)
    return (
      <Stack spacing={2} alignItems="center">
        <Typography variant="h4">Success!</Typography>
        <Typography>Your data has been imported.</Typography>
        <ThemedAgGrid
          columnDefs={[
            { headerName: 'Entity', field: 'entity' },
            { headerName: 'Created', field: 'created' },
            { headerName: 'Skipped', field: 'skipped' },
            {
              headerName: 'Errors',
              field: 'errors',
              cellRenderer: (params: ICellRendererParams<string[]>) => params.value.length,
            },
          ]}
          rowData={importData}
          domLayout="print"
          defaultColDef={{
            resizable: false,
            sortable: false,
          }}
          autoSizeStrategy={{
            type: 'fitCellContents',
          }}
          suppressCellFocus
          suppressRowHoverHighlight
          suppressHorizontalScroll
        />
        {/* <Paper component="code" sx={{ px: 2, overflow: 'auto', maxWidth: '100%' }} variant="outlined">
          <pre>{JSON.stringify(importMutationState.data.ImportFromFile, null, 2)}</pre>
        </Paper> */}
      </Stack>
    )
  }, [importMutationState.data])

  const $stepContent = useMemo(() => {
    if (activeStep === 0) {
      return (
        <Stack spacing={2} alignItems="center">
          <Typography>Download the XLSX template file by clicking the button below.</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            component="a"
            href="/api/static/import_templates/ElasticImportTemplate.xlsx"
            onClick={() => {
              setActiveStep(1)
            }}
          >
            Download Template
          </Button>
        </Stack>
      )
    }
    if (activeStep === 1) {
      return (
        <Stack spacing={2} alignItems="center">
          <Typography>Populate the XLSX template with your data.</Typography>
          <img src={usageGif} alt="Import Template" width="300" />
          <Button variant="contained" color="primary" onClick={() => setActiveStep(2)}>
            Continue
          </Button>
        </Stack>
      )
    }
    if (activeStep === 2) {
      if ($uploadResults) {
        return $uploadResults
      }
      return (
        <Stack spacing={2} alignItems="center">
          <Typography>Upload the populated XLSX template here.</Typography>
          <Button
            disabled={getUploadUrlMutationState.fetching}
            loading={uploading}
            component="label"
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
          >
            Upload Template
            <VisuallyHiddenInput type="file" onChange={handleUploadFile} accept=".xlsx" />
          </Button>
          {getUploadUrlMutationState.error && <Alert severity="error">{getUploadUrlMutationState.error.message}</Alert>}
        </Stack>
      )
    }
  }, [
    activeStep,
    handleUploadFile,
    getUploadUrlMutationState.error,
    getUploadUrlMutationState.fetching,
    uploading,
    $uploadResults,
  ])

  return (
    <Stack spacing={4}>
      <Typography variant="h1" mb={4}>
        Import Your Data
      </Typography>
      <Typography>
        You can import your existing data by downloading a XLSX template file, filling it out, and uploading it here.
      </Typography>
      <Box>
        <Stepper nonLinear activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit" onClick={handleStep(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Paper sx={{ p: 4 }}>{$stepContent}</Paper>
    </Stack>
  )
}
